import React, { useState, useEffect } from 'react';
import { getMyProfile, submitVerificationDocuments } from '../../services/therapistService';
import { uploadFile } from '../../services/uploadService';

const ID_TYPES = [
  { value: 'national_id', label: 'National ID (NIN)' },
  { value: 'drivers_license', label: "Driver's License" },
  { value: 'international_passport', label: 'International Passport' },
  { value: 'voters_card', label: "Voter's Card" }
];

const DOC_TYPE_LABELS = {
  national_id: 'National ID (NIN)',
  drivers_license: "Driver's License",
  international_passport: 'International Passport',
  voters_card: "Voter's Card",
  certification: 'Certification'
};

const VerificationUpload = () => {
  const [profile, setProfile] = useState(null);
  const [idType, setIdType] = useState(ID_TYPES[0].value);
  const [idFile, setIdFile] = useState(null);
  const [certificationFile, setCertificationFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    getMyProfile().then(setProfile);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');

    if (!idFile || !certificationFile) {
      setError('Both an ID document and a certification document are required.');
      return;
    }

    setSubmitting(true);
    try {
      const [idUrl, certificationUrl] = await Promise.all([
        uploadFile(idFile, 'verification-documents'),
        uploadFile(certificationFile, 'verification-documents')
      ]);

      const updated = await submitVerificationDocuments([
        { type: idType, url: idUrl },
        { type: 'certification', url: certificationUrl }
      ]);

      setProfile(updated);
      setMessage('Documents uploaded and submitted for review');
      setIdFile(null);
      setCertificationFile(null);
      e.target.reset();
    } catch (err) {
      setError('Upload failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!profile) return <p>Loading...</p>;

  const status = profile.verification.status;
  const statusColor = { pending: 'text-yellow-600', approved: 'text-green-600', rejected: 'text-red-600' }[status];

  return (
    <div className="bg-white p-6 rounded-lg shadow space-y-4">
      <p>
        Verification status: <span className={`font-semibold ${statusColor}`}>{status}</span>
      </p>
      {status === 'rejected' && profile.verification.rejectionReason && (
        <p className="text-red-600 text-sm">Reason: {profile.verification.rejectionReason}</p>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium mb-1">ID type</label>
          <select
            value={idType}
            onChange={(e) => setIdType(e.target.value)}
            className="w-full p-2 border rounded-lg"
          >
            {ID_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Upload ID document <span className="text-red-500">*</span></label>
          <input
            type="file"
            accept="image/*,.pdf"
            onChange={(e) => setIdFile(e.target.files[0] || null)}
            disabled={submitting}
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Upload certification document <span className="text-red-500">*</span></label>
          <input
            type="file"
            accept="image/*,.pdf"
            onChange={(e) => setCertificationFile(e.target.files[0] || null)}
            disabled={submitting}
            required
          />
        </div>

        {error && <p className="text-sm text-red-600">{error}</p>}
        {message && <p className="text-sm text-green-600">{message}</p>}

        <button
          type="submit"
          disabled={submitting}
          className="bg-black text-white px-4 py-2 rounded-lg hover:bg-gray-800 disabled:opacity-50"
        >
          {submitting ? 'Uploading...' : 'Submit for review'}
        </button>
      </form>

      {profile.verification.documents.length > 0 && (
        <div>
          <p className="text-sm font-medium mb-2">Submitted documents</p>
          <ul className="space-y-1">
            {profile.verification.documents.map((doc, i) => (
              <li key={i} className="text-sm">
                <a href={doc.url} target="_blank" rel="noreferrer" className="text-blue-600 underline">
                  {DOC_TYPE_LABELS[doc.type] || doc.type} — {new Date(doc.uploadedAt).toLocaleDateString()}
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default VerificationUpload;
